import React, { useMemo, useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronLeft, faChevronRight } from "@fortawesome/pro-regular-svg-icons";
import { faCalendarLines } from "@fortawesome/pro-solid-svg-icons";
import Calendar, { CalendarTileProperties } from "react-calendar";
import RoundedRectangle from "../RoundedRectangle";
import { useAvailableDatesSet, useCalendarToggle, useParsedSelectedDate } from "../../../hooks/useDateNavigator";

type CalendarPredicate = (date: Date) => boolean;
const createTileDisabled =
  (isActiveDate: CalendarPredicate) =>
    ({ date, view }: CalendarTileProperties) =>
      view === "month" && !isActiveDate(date);
const createTileClassName =
  (isActiveDate: CalendarPredicate) =>
    ({ date, view }: CalendarTileProperties): string | string[] | null =>
      view !== "month" ? null : isActiveDate(date) ? null : "opacity-50 cursor-not-allowed";

export interface DateNavigatorProps {
  selectedDate: Date | string;
  availableDates: ReadonlyArray<Date | string>;
  label: string;
  onPrev: () => void;
  onNext: () => void;
  onDateSelect: (date: Date) => void;
  className?: string;
  disabled?: boolean;
}


const DateNavigator: React.FC<DateNavigatorProps> = ({
                                                       selectedDate,
                                                       availableDates,
                                                       label,
                                                       onPrev,
                                                       onNext,
                                                       onDateSelect,
                                                       className,
                                                       disabled = false
                                                     }) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const { calendarOpen, toggleCalendar, closeCalendar } = useCalendarToggle({
    containerRef,
    disabled
  });
  const selectedDateObj = useParsedSelectedDate(selectedDate);
  const { hasDate: isActiveYmd } = useAvailableDatesSet(availableDates);

  const handleDaySelect = (date: Date) => {
    if (!isActiveYmd(date)) return;

    onDateSelect(date);
    closeCalendar();
  };

  const tileDisabled = useMemo(() => createTileDisabled(isActiveYmd), [isActiveYmd]);
  const tileClassName = useMemo(() => createTileClassName(isActiveYmd), [isActiveYmd]);

  return (
    <div
      className={`flex flex-col items-start justify-start relative${
        className ? ` ${className}` : ""
      }`}
      ref={containerRef}
    >
      <NavigatorHeader
        label={label}
        disabled={disabled}
        onPrev={disabled ? undefined : onPrev}
        onNext={disabled ? undefined : onNext}
        onToggleCalendar={toggleCalendar}
      />

      <NavigatorCalendar
        isOpen={calendarOpen}
        selectedDate={selectedDateObj}
        onDaySelect={handleDaySelect}
        tileDisabled={tileDisabled}
        tileClassName={tileClassName}
      />
    </div>
  );
};

interface NavigatorHeaderProps {
  label: string;
  disabled: boolean;
  onPrev?: () => void;
  onNext?: () => void;
  onToggleCalendar: () => void;
}

const NavigatorHeader: React.FC<NavigatorHeaderProps> = ({
                                                           label,
                                                           disabled,
                                                           onPrev,
                                                           onNext,
                                                           onToggleCalendar
                                                         }) => (
  <RoundedRectangle
    className="w-full h-14 border border-light-gray dark:border-light-gray-dark bg-bg-secondary dark:bg-bg-secondary-dark"
    hasBorder
    hasTitle={false}
  >
    <div className="w-full h-full flex items-center px-3">
      <button
        type="button"
        aria-label="Previous date"
        onClick={onPrev}
        disabled={disabled}
        className="p-2 rounded-md hover:bg-input-bg dark:hover:bg-input-bg-dark disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <FontAwesomeIcon icon={faChevronLeft} className="w-4 h-4" />
      </button>
      <div className="flex-1 flex flex-row items-center justify-center">
        <span className="text-base font-regular text-font-600 dark:text-font-600-dark mr-2">
          {label ?? ""}
        </span>
      </div>
      <button
        type="button"
        aria-label="Next date"
        onClick={onNext}
        disabled={disabled}
        className="p-2 rounded-md hover:bg-input-bg dark:hover:bg-input-bg-dark disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <FontAwesomeIcon icon={faChevronRight} className="w-4 h-4" />
      </button>

      <button
        type="button"
        aria-label="Open calendar"
        aria-haspopup="dialog"
        onClick={onToggleCalendar}
        disabled={disabled}
        className="p-2 rounded-md hover:bg-input-bg dark:hover:bg-input-bg-dark disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <FontAwesomeIcon icon={faCalendarLines} className="text-font-600 dark:text-font-600-dark" />
      </button>
    </div>
  </RoundedRectangle>
);

interface NavigatorCalendarProps {
  isOpen: boolean;
  selectedDate: Date;
  onDaySelect: (date: Date) => void;
  tileDisabled: ReturnType<typeof createTileDisabled>;
  tileClassName: ReturnType<typeof createTileClassName>;
}

const NavigatorCalendar: React.FC<NavigatorCalendarProps> = ({
                                                               isOpen,
                                                               selectedDate,
                                                               onDaySelect,
                                                               tileDisabled,
                                                               tileClassName
                                                             }) => {

  if (!isOpen) {
    return null;
  }

  return (
    <div className="absolute right-2 top-full mt-1 z-50 flex justify-end" role="dialog" aria-modal="true">
      <div className="max-w-96 w-full">
        <Calendar
          onClickDay={onDaySelect}
          value={selectedDate}
          className="w-full bg-bg-secondary dark:bg-bg-secondary-dark rounded-xl shadow-shadow dark:shadow-shadow-dark drop-shadow-xl p-4"
          tileDisabled={tileDisabled}
          tileClassName={tileClassName}
          prev2Label={null}
          next2Label={null}
          minDetail="month"
          maxDetail="month"
        />
      </div>
    </div>
  );
};

export default DateNavigator;

